/**
 * 智能体选择器组件
 *
 * 提供智能体切换功能，显示当前选中的智能体
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAgentStore } from '../../store/agentStore';
import type { Agent } from '../../types/agent';

interface AgentSelectorProps {
  disabled?: boolean;
}

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const RobotIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

export const AgentSelector: React.FC<AgentSelectorProps> = ({ disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    agents,
    currentAgentId,
    setCurrentAgent,
    fetchAgents,
    getCurrentAgent,
  } = useAgentStore();

  // 加载智能体列表
  useEffect(() => {
    if (agents.length === 0) {
      fetchAgents();
    }
  }, [agents.length, fetchAgents]);

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 获取当前智能体
  const currentAgent = getCurrentAgent();

  // 处理选择智能体
  const handleSelectAgent = useCallback((agent: Agent) => {
    setCurrentAgent(agent.id);
    setIsOpen(false);
  }, [setCurrentAgent]);

  // 分组智能体（过滤掉 subagent 模式的智能体，只显示 primary 和 all）
  const selectableAgents = agents.filter(a => a.mode !== 'subagent');
  const systemAgents = selectableAgents.filter(a => a.is_system);
  const userAgents = selectableAgents.filter(a => !a.is_system);

  return (
    <div ref={dropdownRef} className="relative">
      {/* 选择器按钮 */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="选择智能体"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <RobotIcon className="w-4 h-4 text-gray-600" />
        <span className="max-w-[100px] truncate">
          {currentAgent?.name || '默认'}
        </span>
        <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* 下拉菜单 */}
      {isOpen && (
        <div
          className="absolute bottom-full mb-2 left-0 w-64 bg-white border border-gray-200 rounded-lg shadow-sm z-50 overflow-hidden"
          role="listbox"
        >
          {/* 系统智能体 */}
          {systemAgents.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50">
                系统智能体
              </div>
              {systemAgents.map((agent) => (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => handleSelectAgent(agent)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                    currentAgentId === agent.id ? 'bg-paper-50 text-paper-800' : 'text-gray-900'
                  }`}
                  role="option"
                  aria-selected={currentAgentId === agent.id}
                >
                  <span className="truncate">{agent.name}</span>
                  <span className="text-xs text-gray-400">T={agent.temperature}</span>
                </button>
              ))}
            </div>
          )}

          {/* 用户自定义智能体 */}
          {userAgents.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50 border-t border-gray-100">
                自定义智能体
              </div>
              {userAgents.map((agent) => (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => handleSelectAgent(agent)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                    currentAgentId === agent.id ? 'bg-paper-50 text-paper-800' : 'text-gray-900'
                  }`}
                  role="option"
                  aria-selected={currentAgentId === agent.id}
                >
                  <span className="truncate">{agent.name}</span>
                  <span className="text-xs text-gray-400">T={agent.temperature}</span>
                </button>
              ))}
            </div>
          )}

          {/* 空状态 */}
          {agents.length === 0 && (
            <div className="px-3 py-4 text-center text-sm text-gray-500">
              暂无可用智能体
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentSelector;
